/**
 * UploadedFileViewer — cuerpo del visor oficial para artifacts en
 * `source_mode='upload'` (OT-0007). Se monta en el slot `bodySlot` de
 * `ArtifactPreview` — reemplaza el árbol de secciones (no hay secciones
 * que mostrar: el contenido del documento ES el archivo, `version.content`
 * se queda en `{}`). El header/badge/metadata de `ArtifactPreview` no
 * cambian — solo el cuerpo.
 *
 * Estados:
 *   - Sin archivo: empty state + zona de carga (si el actor puede editar).
 *   - Con archivo: card con nombre/tipo/tamaño + descargar + reemplazar
 *     (subir de nuevo) / borrar, gateado por mutabilidad + tier.
 *
 * Mutabilidad: `version.is_editable`, calculado por el backend con su
 * regla única (`is_version_mutable`, OT-0064). Si una subida o un borrado
 * llega con la versión ya bloqueada, el 409 (`ARTIFACT_IN_REVIEW` o
 * `ARTIFACT_VERSION_NOT_DRAFT`) la marca bloqueada y los controles se
 * ocultan sin esperar al refetch.
 *
 * `canManageFile` (prop): espejo de `can_manage_project_sources` del
 * backend — tier de edición Y (miembro de este proyecto O admin del
 * tenant). Lo resuelve el caller (necesita la lista de miembros del
 * proyecto); este componente solo lo combina con la mutabilidad.
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, FileText, Trash2, Upload } from 'lucide-react'

import type { Artifact, ArtifactVersion } from '../../../api/artifacts'
import {
  deleteArtifactVersionFile,
  downloadArtifactVersionFile,
  uploadArtifactVersionFile,
} from '../../../api/artifacts'
import { ApiError } from '../../../api/client'
import { triggerBrowserDownload } from '../../../api/download'
import { useAccessToken } from '../../../auth/useAccessToken'
import { isVersionLockError, markVersionLocked } from '../../../lib/versionLock'
import { pushToast } from '../../../stores/toastStore'

export function UploadedFileViewer({
  artifact,
  version,
  canManageFile,
}: {
  artifact: Artifact
  version: ArtifactVersion
  canManageFile: boolean
}) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const blobRef = version.blob_refs[0] ?? null
  const canManage = canManageFile && version.is_editable

  function invalidate() {
    void queryClient.invalidateQueries({
      queryKey: ['artifacts', artifact.id],
    })
    void queryClient.invalidateQueries({
      queryKey: ['artifacts', artifact.id, 'versions', version.id],
    })
  }

  function reportError(err: unknown, fallbackKey: string) {
    if (isVersionLockError(err)) {
      markVersionLocked(queryClient, artifact.id, version.id)
    }
    const code = err instanceof ApiError ? err.code : null
    pushToast({
      kind: 'critical',
      message: code
        ? t(`errors:${code}` as 'errors:UNKNOWN', {
            defaultValue: t(fallbackKey as 'official_viewer.uploaded_file.upload_label'),
          })
        : t(fallbackKey as 'official_viewer.uploaded_file.upload_label'),
      duration: 6000,
    })
  }

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!accessToken) throw new Error('no_access_token')
      return uploadArtifactVersionFile(
        accessToken,
        artifact.id,
        version.id,
        file,
      )
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('official_viewer.uploaded_file.toast_upload_success'),
      })
      invalidate()
    },
    onError: (err) =>
      reportError(err, 'official_viewer.uploaded_file.toast_upload_failed'),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return deleteArtifactVersionFile(accessToken, artifact.id, version.id)
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('official_viewer.uploaded_file.toast_delete_success'),
      })
      invalidate()
    },
    onError: (err) =>
      reportError(err, 'official_viewer.uploaded_file.toast_delete_failed'),
  })

  async function handleDownload() {
    if (!accessToken || !blobRef) return
    setIsDownloading(true)
    try {
      const { blob, filename } = await downloadArtifactVersionFile(
        accessToken,
        artifact.id,
        version.id,
      )
      triggerBrowserDownload(blob, filename ?? blobRef.filename)
    } catch (err) {
      reportError(err, 'official_viewer.uploaded_file.toast_download_failed')
    } finally {
      setIsDownloading(false)
    }
  }

  function handleFileSelected(file: File | null) {
    if (!file) return
    uploadMutation.mutate(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className="space-y-4 max-w-2xl">
      {blobRef ? (
        <div className="flex items-center gap-4 rounded-md border border-line bg-surface p-4">
          <FileText
            className="w-8 h-8 text-brand shrink-0"
            aria-hidden="true"
          />
          <div className="flex-1 min-w-0">
            <p className="font-body text-sm text-ink truncate">
              {blobRef.filename}
            </p>
            <p className="mt-0.5 font-mono text-[11px] text-ink-subtle">
              {blobRef.mime_type} · {formatFileSize(blobRef.size_bytes)}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              title={t('official_viewer.uploaded_file.download')}
              className="p-2 rounded-md text-ink-muted hover:text-ink hover:bg-surface-muted disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
            </button>
            {canManage ? (
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                title={t('official_viewer.uploaded_file.delete')}
                className="p-2 rounded-md text-ink-muted hover:text-critical hover:bg-critical-soft disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-line bg-surface-muted/40 px-6 py-10 text-center">
          <FileText
            className="w-8 h-8 text-ink-subtle mx-auto mb-2"
            aria-hidden="true"
          />
          <p className="font-body text-sm text-ink-muted">
            {t('official_viewer.uploaded_file.empty')}
          </p>
        </div>
      )}

      {canManage ? (
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {blobRef
              ? t('official_viewer.uploaded_file.replace_label')
              : t('official_viewer.uploaded_file.upload_label')}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
            disabled={uploadMutation.isPending}
            className="mt-1 w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-brand-soft file:text-brand hover:file:bg-brand-soft/70 file:cursor-pointer disabled:opacity-50"
          />
          {uploadMutation.isPending ? (
            <span className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-subtle">
              <Upload className="w-3 h-3 animate-pulse" aria-hidden="true" />
              {t('official_viewer.uploaded_file.uploading')}
            </span>
          ) : null}
        </label>
      ) : null}
    </div>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
